import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import useFetch from "../../hooks/useFetch";

const ROTULOS = {
    sala: "Sala",
    estante: "Estante",
    prateleira: "Prateleira",
    caixa: "Caixa",
    envelope_pasta: "Envelope / Pasta",
};

// Nome do array retornado por GET /distinct_localizacao para cada nível
const PLURAL = {
    sala: "salas",
    estante: "estantes",
    prateleira: "prateleiras",
    caixa: "caixas",
    envelope_pasta: "envelope_pastas",
};

const OPCOES_VAZIAS = { salas: [], estantes: [], prateleiras: [], caixas: [], envelope_pastas: [] };

/**
 * Seletor em cascata de Localização física do acervo (Sala -> Estante ->
 * Prateleira -> [Caixa -> Envelope/Pasta]).
 *
 * Escolher um nível recarrega as opções dos níveis seguintes filtradas pelo
 * que já foi escolhido (GET /distinct_localizacao), ao invés de mostrar uma
 * lista fixa com todas as combinações possíveis.
 *
 * Props:
 * - niveis: quais campos mostrar, em ordem. Ex.: ["sala","estante","prateleira"]
 *   para Itens, ou mais "caixa"/"envelope_pasta" para Fotografias.
 * - valores: objeto controlado { sala, estante, prateleira, caixa, envelope_pasta, id_localizacao }
 * - onChange(novosValores): chamado sempre que a seleção muda ou uma nova
 *   localização é cadastrada pelo modal. novosValores sempre traz todos os
 *   campos de `niveis` (string, "" se vazio) + id_localizacao (número quando a
 *   combinação bate com um registro já existente, senão null).
 * - disabled: desabilita toda a seleção (modo "Visualizar").
 */
export default function LocalizacaoSelector({ niveis, valores, onChange, disabled }) {
    const { get, post } = useFetch();
    const [opcoes, setOpcoes] = useState(OPCOES_VAZIAS);
    const [showModal, setShowModal] = useState(false);
    const [novaLocalizacao, setNovaLocalizacao] = useState({});

    const carregarOpcoes = useCallback(async (filtros) => {
        const query = Object.entries(filtros)
            .filter(([, v]) => !!v)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join("&");
        const response = await get(`/distinct_localizacao${query ? `?${query}` : ""}`);
        return response.ok ? { ...OPCOES_VAZIAS, ...response.result.data } : OPCOES_VAZIAS;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recarrega as opções de TODOS os níveis, cada um filtrado só pelos níveis
    // ACIMA dele (nunca por si mesmo ou pelos de baixo) — o backend calcula os
    // distinct de sala/estante/prateleira/caixa/envelope usando o MESMO filtro
    // pra tudo, então se a gente reaproveitasse uma única resposta pra todos os
    // níveis, a lista de Sala acabava filtrada pela Estante/Caixa já escolhidas
    // e sumiam as outras salas cadastradas (era exatamente esse o bug: com uma
    // fotografia já editada e todos os níveis preenchidos, a 1ª chamada saía
    // com sala+estante+prateleira+caixa+envelope todos no filtro, e o "distinct
    // sala" resultante só podia ser o próprio registro).
    const recarregarOpcoes = useCallback(async (valoresAtuais) => {
        const respostas = await Promise.all(
            niveis.map((_, idx) => {
                const filtros = {};
                for (let i = 0; i < idx; i++) filtros[niveis[i]] = valoresAtuais?.[niveis[i]];
                return carregarOpcoes(filtros);
            })
        );
        let novasOpcoes = { ...OPCOES_VAZIAS };
        niveis.forEach((nivel, idx) => {
            novasOpcoes[PLURAL[nivel]] = respostas[idx][PLURAL[nivel]];
        });
        return novasOpcoes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregarOpcoes, niveis]);

    // Ao montar (inclusive quando os valores externos já vêm preenchidos, ex.:
    // edição de um registro existente), carrega as opções de cada nível
    // corretamente (sala sempre com a lista completa, etc.).
    useEffect(() => {
        recarregarOpcoes(valores).then(setOpcoes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tenta descobrir o id_localizacao exato da combinação atual. Só faz
    // sentido quando todos os níveis relevantes estão preenchidos.
    async function resolverId(novosValores) {
        if (niveis.some((nivel) => !novosValores[nivel])) {
            return null;
        }
        const query = niveis.map((nivel) => `${nivel}=${encodeURIComponent(novosValores[nivel])}`).join("&");
        const response = await get(`/localizacao?${query}`);
        if (!response.ok) return null;
        const encontrado = (response.result.data ?? []).find((loc) =>
            niveis.every((nivel) => (loc[nivel] ?? "") === (novosValores[nivel] ?? ""))
        );
        return encontrado?.id_localizacao ?? null;
    }

    async function alterarNivel(nivel, valor) {
        const idx = niveis.indexOf(nivel);
        let novosValores = { ...valores, [nivel]: valor };
        // limpa os níveis abaixo do que foi alterado (ex.: trocar a Sala zera
        // Estante/Prateleira já escolhidas, que podem não existir na sala nova)
        for (let i = idx + 1; i < niveis.length; i++) {
            novosValores[niveis[i]] = "";
        }

        recarregarOpcoes(novosValores).then(setOpcoes);

        const id_localizacao = await resolverId(novosValores);
        onChange({ ...novosValores, id_localizacao });
    }

    function abrirModal() {
        // pré-preenche o modal com o que já foi escolhido nos níveis acima
        let inicial = {};
        niveis.forEach((nivel) => { inicial[nivel] = valores?.[nivel] || ""; });
        setNovaLocalizacao(inicial);
        setShowModal(true);
    }

    async function salvarNovaLocalizacao() {
        const response = await post("/localizacao", novaLocalizacao);
        if (response.status === 201) {
            const novoRegistro = response.result.data;
            setShowModal(false);

            let novosValores = {};
            niveis.forEach((nivel) => { novosValores[nivel] = novoRegistro[nivel] || ""; });

            recarregarOpcoes(novosValores).then(setOpcoes);

            onChange({ ...novosValores, id_localizacao: novoRegistro.id_localizacao });
        } else {
            Swal.fire({
                title: "Não foi possível cadastrar a localização",
                text: response.result?.message ?? "Erro ao salvar",
                icon: "error",
                confirmButtonColor: "#587b7f",
            });
        }
    }

    return (
        <>
            <div className="row g-3 p-3 bg-light rounded-4 border">
                {niveis.map((nivel) => (
                    <Form.Group className="col-md-4" key={nivel}>
                        <Form.Label className="label-admin">{ROTULOS[nivel]}</Form.Label>
                        <Form.Select
                            className="input-moderno"
                            value={valores?.[nivel] || ""}
                            onChange={(e) => alterarNivel(nivel, e.target.value)}
                            disabled={disabled}
                        >
                            <option value="">Escolha...</option>
                            {(opcoes[PLURAL[nivel]] ?? []).map((v, i) => (
                                <option key={i} value={v}>{v}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                ))}
                {!disabled && (
                    <div className="col-12 text-end">
                        <Button variant="link" size="sm" onClick={abrirModal}>+ Nova localização</Button>
                    </div>
                )}
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white">Adicionar localização</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {niveis.map((nivel) => (
                        <Form.Group className="mb-3" key={nivel}>
                            <Form.Label className="label-admin">{ROTULOS[nivel]}</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder={ROTULOS[nivel]}
                                value={novaLocalizacao[nivel] || ""}
                                onChange={(e) => setNovaLocalizacao((prev) => ({ ...prev, [nivel]: e.target.value }))}
                            />
                        </Form.Group>
                    ))}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setShowModal(false)}>Cancelar</Button>
                    <Button className="btn-apply-filters" onClick={salvarNovaLocalizacao}>Salvar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
